import React, { useEffect, useMemo, useState } from "react";
import { Pencil, Plus, Power, RefreshCw, Trash2 } from "lucide-react";
import AdminLayout from "./AdminLayout";
import API from "../services/api";
import { getMarketWindowForNow } from "../utils/marketStatus";

const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function formatMarketName(value = "") {
  return String(value || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trimStart()
    .replace(/\b([a-z])/g, (match) => match.toUpperCase());
}

function createEmptyException(day = "Sun", open_time = "", close_time = "", closed = false) {
  return { day, open_time, close_time, closed };
}

function getNextAvailableDay(items = []) {
  const used = new Set(items.map((item) => String(item?.day || item).slice(0, 3)));
  return weekDays.find((day) => !used.has(day)) || weekDays[0];
}

function normalizeSchedule(value, openTime = "", closeTime = "") {
  let parsed = [];
  if (Array.isArray(value)) {
    parsed = value;
  } else if (value && typeof value === "object") {
    if (Array.isArray(value.days)) parsed = value.days;
    else parsed = Object.entries(value).map(([day, entry]) => ({ day, ...entry }));
  } else if (typeof value === "string" && value.trim()) {
    try {
      const json = JSON.parse(value);
      if (Array.isArray(json)) parsed = json;
      else if (json && Array.isArray(json.days)) parsed = json.days;
      else if (json && typeof json === "object") parsed = Object.entries(json).map(([day, entry]) => ({ day, ...entry }));
    } catch {
      parsed = [];
    }
  }

  return weekDays.map((day) => {
    const found = parsed.find((item) => String(item?.day || "").slice(0, 3).toLowerCase() === day.toLowerCase());
    return {
      day,
      open_time: found?.open_time || found?.open || openTime || "",
      close_time: found?.close_time || found?.close || closeTime || "",
      closed: Boolean(found?.closed || found?.is_closed || found?.enabled === false),
    };
  });
}

function extractExceptions(schedule = [], openTime = "", closeTime = "", closedDays = []) {
  return schedule
    .filter((item) => {
      const defaultClosed = closedDays.includes(item.day);
      return item.closed || defaultClosed || item.open_time !== openTime || item.close_time !== closeTime;
    })
    .map((item) => ({
      day: item.day,
      open_time: item.open_time,
      close_time: item.close_time,
      closed: Boolean(item.closed),
    }));
}

function buildSchedulePayload(form) {
  const baseOpen = String(form.open_time || "").trim();
  const baseClose = String(form.close_time || "").trim();
  const closedDays = Array.isArray(form.closed_days) ? form.closed_days : [];
  const exceptions = Array.isArray(form.exceptions) ? form.exceptions : [];
  const usedDays = new Set();
  const rows = [];

  exceptions.forEach((item) => {
    const day = String(item?.day || "").slice(0, 3);
    if (!weekDays.includes(day) || usedDays.has(day)) return;
    const openTime = String(item.open_time || baseOpen || "").trim();
    const closeTime = String(item.close_time || baseClose || "").trim();
    const isClosed = closedDays.includes(day) || Boolean(item.closed);
    const changed = isClosed || openTime !== baseOpen || closeTime !== baseClose;
    if (!changed) return;
    usedDays.add(day);
    rows.push({ day, open_time: openTime, close_time: closeTime, closed: isClosed });
  });

  closedDays.forEach((day) => {
    if (!weekDays.includes(day) || usedDays.has(day)) return;
    usedDays.add(day);
    rows.push({ day, open_time: baseOpen, close_time: baseClose, closed: true });
  });

  return JSON.stringify(rows);
}

function getScheduleSummary(market) {
  const schedule = normalizeSchedule(market.schedule_json, market.open_time || "", market.close_time || "");
  const custom = schedule.filter((item) => item.open_time !== (market.open_time || "") || item.close_time !== (market.close_time || "") || item.closed);
  if (!custom.length) {
    return `${formatTime(market.open_time)} - ${formatTime(market.close_time)}`;
  }

  return custom
    .slice(0, 2)
    .map((item) => `${item.day}: ${item.closed ? "Closed" : `${formatTime(item.open_time)} - ${formatTime(item.close_time)}`}`)
    .join(" • ");
}

function getTodayWindowLabel(market) {
  const current = getMarketWindowForNow(market);
  if (current.closed) return `${current.day}: Closed`;
  return `${current.day}: ${formatTime(current.open_time || market.open_time)} - ${formatTime(current.close_time || market.close_time)}`;
}

function AdminMarkets() {
  const [markets, setMarkets] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm);
  const normalMarkets = useMemo(
    () => markets
      .filter((item) => (item.market_type || "normal") !== "starline")
      .slice()
      .sort(sortMarketsByTime),
    [markets]
  );

  useEffect(() => {
    loadMarkets();
  }, []);

  const stats = useMemo(() => ({
    total: normalMarkets.length,
    active: normalMarkets.filter((item) => item.is_active !== 0).length,
    inactive: normalMarkets.filter((item) => item.is_active === 0).length,
  }), [normalMarkets]);

  const loadMarkets = async () => {
    try {
      const res = await API.get("/admin/markets");
      setMarkets(Array.isArray(res.data) ? res.data : []);
    } catch {
      setMarkets([]);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...initialForm,
      exceptions: [],
    });
    setOpen(true);
  };

  const openEdit = (market) => {
    setEditing(market);
    setForm({
      name: market.name || "",
      open_time: market.open_time || "",
      close_time: market.close_time || "",
      closed_days: market.closed_days ? String(market.closed_days).split(",").filter(Boolean) : [],
      exceptions: extractExceptions(
        normalizeSchedule(market.schedule_json, market.open_time || "", market.close_time || ""),
        market.open_time || "",
        market.close_time || "",
        market.closed_days ? String(market.closed_days).split(",").filter(Boolean) : []
      ),
      is_active: market.is_active !== 0,
      market_type: market.market_type || "normal",
    });
    setOpen(true);
  };

  const toggleDay = (day) => {
    setForm((current) => ({
      ...current,
      closed_days: current.closed_days.includes(day)
        ? current.closed_days.filter((item) => item !== day)
        : [...current.closed_days, day],
    }));
  };

  const saveMarket = async () => {
    const payload = {
      ...form,
      name: formatMarketName(form.name),
      bid_close_time: form.close_time,
      schedule_json: buildSchedulePayload(form),
      market_type: "normal",
    };

    try {
      if (editing?.id) {
        await API.put(`/admin/markets/${editing.id}`, payload);
      } else {
        await API.post("/admin/markets", payload);
      }
      setOpen(false);
      await loadMarkets();
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to save market");
    }
  };

  const deleteMarket = async (market) => {
    const marketName = market?.name || "this market";
    const ok = window.confirm(`Delete ${marketName}? This cannot be undone.`);
    if (!ok) return;

    try {
      await API.delete(`/admin/markets/${market.id}`);
      await loadMarkets();
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to delete market");
    }
  };

  const toggleMarketStatus = async (market) => {
    try {
      await API.put(`/admin/markets/${market.id}`, {
        ...market,
        bid_close_time: market.close_time,
        is_active: market.is_active === 0,
      });
      await loadMarkets();
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to update status");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await saveMarket();
  };

  return (
    <AdminLayout>
      <section style={page}>
        <div style={pageHeader} className="admin-market-header">
          <div>
            <h1 style={title}>Game Control</h1>
            <p style={subtitle}>Manage market timings, closed days, and instant active/inactive control.</p>
          </div>

          <div style={headerButtons} className="admin-market-header-buttons">
            <button type="button" style={refreshButton} className="admin-market-refresh-button" onClick={loadMarkets}><RefreshCw size={16} /> <span>Refresh</span></button>
            <button type="button" style={createButton} className="admin-market-create-button" onClick={openCreate}><Plus size={18} /> Create Market</button>
          </div>
        </div>

        <div style={statsGrid} className="admin-market-stats-grid">
          <div style={statCard}><span>Total Markets</span><strong>{stats.total}</strong><small>All markets</small></div>
          <div style={{ ...statCard, borderColor: "#bbf7d0", background: "#f0fdf4" }}><span>Enabled</span><strong style={{ color: "#16a34a" }}>{stats.active}</strong><small>Available in system</small></div>
          <div style={statCard}><span>Disabled</span><strong>{stats.inactive}</strong><small>Hidden from system</small></div>
        </div>

        <div style={tableCard}>
          <table style={table}>
            <thead>
              <tr>
                <th>Market</th>
                <th>Timings</th>
                <th>Closed Days</th>
                <th>System Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {normalMarkets.map((market, index) => (
                <tr key={market.id}>
                  <td>
                    <div style={marketCell}>
                      <span style={{ ...marketLogo, background: marketColors[index % marketColors.length] }}>{getInitials(market.name)}</span>
                      <span>
                        <strong style={marketName}>{market.name}</strong>
                        <small style={marketSub}>{getTodayWindowLabel(market)}</small>
                      </span>
                    </div>
                  </td>
                  <td style={{ maxWidth: 260, lineHeight: 1.5 }}><span style={timePillOpen}>{getScheduleSummary(market)}</span></td>
                  <td>{market.closed_days || "None"}</td>
                  <td>
                    <button type="button" style={market.is_active !== 0 ? activeBadge : inactiveBadge} onClick={() => toggleMarketStatus(market)}>
                      <Power size={13} /> {market.is_active !== 0 ? "Enabled" : "Disabled"}
                    </button>
                  </td>
                  <td>
                    <div style={actionRow}>
                      <button type="button" style={editButton} onClick={() => openEdit(market)}><Pencil size={16} /></button>
                      <button type="button" style={deleteButton} onClick={() => deleteMarket(market)}><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {open && (
          <div style={overlay} className="admin-market-modal-overlay">
            <div style={modal}>
              <div style={modalHeader}>
                <div>
                  <h2 style={modalTitle}>{editing ? "Edit Market" : "Create Market"}</h2>
                  <p style={modalSubtitle}>{editing ? `Editing ${editing.name}` : "Add a new market"}</p>
                </div>
                <button type="button" style={closeButton} onClick={() => setOpen(false)}>x</button>
              </div>

              <form style={modalBody} className="admin-market-modal-body" onSubmit={handleSubmit}>
                <label style={fieldLabel}>Market Name</label>
                <input style={fieldInput} value={form.name} onChange={(e) => setForm((c) => ({ ...c, name: formatMarketName(e.target.value) }))} />

                <div style={timeGrid} className="admin-market-time-grid">
                  <div>
                    <label style={fieldLabel}>Open</label>
                    <input
                      type="time"
                      style={fieldInput}
                      value={form.open_time}
                      onChange={(e) => setForm((current) => {
                        const nextOpen = e.target.value;
                        return {
                          ...current,
                          open_time: nextOpen,
                          exceptions: (current.exceptions || []).map((item) =>
                            item.closed ? item : { ...item, open_time: item.open_time || nextOpen }
                          ),
                        };
                      })}
                    />
                  </div>
                  <div>
                    <label style={fieldLabel}>Close</label>
                    <input
                      type="time"
                      style={fieldInput}
                      value={form.close_time}
                      onChange={(e) => setForm((current) => {
                        const nextClose = e.target.value;
                        return {
                          ...current,
                          close_time: nextClose,
                          exceptions: (current.exceptions || []).map((item) =>
                            item.closed ? item : { ...item, close_time: item.close_time || nextClose }
                          ),
                        };
                      })}
                    />
                  </div>
                </div>
                <div style={timingHint}>
                  OPEN bids stop at open time. CLOSE/Jodi/Sangam bids stop at close time automatically.
                </div>

                <div style={scheduleActions}>
                  <button
                    type="button"
                    style={addExceptionButton}
                    onClick={() => setForm((current) => ({
                      ...current,
                      exceptions: [...(current.exceptions || []), createEmptyException(getNextAvailableDay(current.exceptions || []), current.open_time, current.close_time, false)],
                    }))}
                  >
                    <Plus size={14} /> Add Custom Day
                  </button>
                </div>

                {(form.exceptions || []).length > 0 && (
                  <div style={scheduleHeader}>
                    <strong style={scheduleTitle}>Custom Days</strong>
                    <span style={scheduleSubtitle}>Only days listed here use different timing or closed status.</span>
                  </div>
                )}

                {(form.exceptions || []).length > 0 && <div style={exceptionList}>
                  {(form.exceptions || []).map((item, index) => (
                    <div key={`${item.day}-${index}`} style={exceptionRow} className="admin-market-exception-row">
                      <select
                        style={exceptionDayInput}
                        value={item.day}
                        onChange={(e) => setForm((current) => {
                          const next = [...(current.exceptions || [])];
                          next[index] = { ...next[index], day: e.target.value };
                          return { ...current, exceptions: next };
                        })}
                      >
                        {weekDays.map((day) => <option key={day} value={day}>{day}</option>)}
                      </select>
                      <input
                        type="time"
                        style={exceptionTimeInput}
                        value={item.open_time}
                        onChange={(e) => setForm((current) => {
                          const next = [...(current.exceptions || [])];
                          next[index] = { ...next[index], open_time: e.target.value };
                          return { ...current, exceptions: next };
                        })}
                      />
                      <input
                        type="time"
                        style={exceptionTimeInput}
                        value={item.close_time}
                        onChange={(e) => setForm((current) => {
                          const next = [...(current.exceptions || [])];
                          next[index] = { ...next[index], close_time: e.target.value };
                          return { ...current, exceptions: next };
                        })}
                      />
                      <label style={exceptionClosed}>
                        <input
                          type="checkbox"
                          checked={Boolean(item.closed)}
                          onChange={(e) => setForm((current) => {
                            const next = [...(current.exceptions || [])];
                            next[index] = { ...next[index], closed: e.target.checked };
                            return { ...current, exceptions: next };
                          })}
                        />
                        Closed
                      </label>
                      <button
                        type="button"
                        style={removeExceptionButton}
                        onClick={() => setForm((current) => ({
                          ...current,
                          exceptions: (current.exceptions || []).filter((_, i) => i !== index),
                        }))}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>}

                <div style={scheduleHeader}>
                  <strong style={scheduleTitle}>Closed Days</strong>
                  <span style={scheduleSubtitle}>Add only the days this market should stay closed.</span>
                </div>
                <div style={closedDayRow}>
                  {(form.closed_days || []).map((day) => (
                    <button key={day} type="button" style={dayActive} onClick={() => toggleDay(day)}>
                      {day} x
                    </button>
                  ))}
                  <select
                    style={closedDaySelect}
                    value=""
                    onChange={(event) => {
                      if (event.target.value) toggleDay(event.target.value);
                    }}
                  >
                    <option value="">Add Day</option>
                    {weekDays.filter((day) => !form.closed_days.includes(day)).map((day) => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                </div>

                <label style={checkboxRow}>
                  <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((c) => ({ ...c, is_active: e.target.checked }))} />
                  Market active
                </label>

                <div style={footerRow} className="admin-market-footer-row">
                  <button type="button" style={cancelButton} onClick={() => setOpen(false)}>Cancel</button>
                  <button type="submit" style={updateButton}>{editing ? "Update" : "Create"}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </section>
    </AdminLayout>
  );
}

const initialForm = {
  name: "",
  open_time: "",
  close_time: "",
  closed_days: [],
  exceptions: [],
  is_active: true,
  market_type: "normal",
};

const page = { display: "grid", gap: "20px", fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif" };
const pageHeader = { display: "flex", justifyContent: "space-between", alignItems: "center" };
const title = { margin: 0, fontSize: "20px", fontWeight: 800 };
const subtitle = { margin: "6px 0 0", color: "#64748b" };
const headerButtons = { display: "flex", gap: "10px" };
const refreshButton = { width: "46px", height: "46px", border: "1px solid #d1d5db", background: "#fff", borderRadius: "12px", cursor: "pointer" };
const createButton = { height: "46px", padding: "0 20px", border: 0, borderRadius: "12px", background: "#316ce8", color: "#fff", fontWeight: 700, display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" };
const statsGrid = { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "16px" };
const statCard = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "20px", padding: "20px 24px", display: "grid", gap: "10px" };
const tableCard = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "20px", overflow: "auto", boxShadow: "0 12px 34px rgba(15,23,42,0.06)" };
const table = { width: "100%", minWidth: "980px", borderCollapse: "separate", borderSpacing: 0 };
const marketCell = { display: "flex", alignItems: "center", gap: "12px", padding: "10px 0" };
const marketLogo = { width: "42px", height: "42px", display: "grid", placeItems: "center", color: "#fff", fontWeight: 900, fontSize: "13px", borderRadius: "14px", boxShadow: "0 8px 16px rgba(15,23,42,0.12)" };
const marketName = { display: "block", color: "#0f172a", fontSize: "15px", fontWeight: 900 };
const marketSub = { display: "block", marginTop: "3px", color: "#94a3b8", fontSize: "11px", fontWeight: 700 };
const timePillOpen = { display: "inline-block", padding: "7px 10px", background: "#ecfdf5", color: "#059669", border: "1px solid #bbf7d0", fontWeight: 800, fontSize: "12px", borderRadius: "10px" };
const actionRow = { display: "flex", gap: "8px" };
const editButton = { width: "42px", height: "42px", border: 0, background: "#dbeafe", color: "#2563eb", borderRadius: "10px", cursor: "pointer" };
const deleteButton = { width: "42px", height: "42px", border: 0, background: "#fee2e2", color: "#dc2626", borderRadius: "10px", cursor: "pointer" };
const activeBadge = { border: 0, background: "#22c55e", color: "#fff", padding: "9px 16px", borderRadius: "999px", fontWeight: 800, display: "inline-flex", alignItems: "center", gap: "7px", cursor: "pointer", boxShadow: "0 8px 16px rgba(34,197,94,0.20)" };
const inactiveBadge = { ...activeBadge, background: "#e5e7eb", color: "#475569", boxShadow: "none" };
const overlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "grid", placeItems: "center", zIndex: 1000, padding: "16px", overflowY: "auto" };
const modal = { width: "690px", maxWidth: "92vw", maxHeight: "calc(100vh - 32px)", background: "#fff", borderRadius: "24px", overflow: "hidden", boxShadow: "0 30px 80px rgba(15,23,42,0.28)", display: "grid", gridTemplateRows: "auto minmax(0,1fr)" };
const modalHeader = { padding: "28px 28px 22px", background: "linear-gradient(90deg, #316ce8, #5b3df0)", color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "flex-start" };
const modalTitle = { margin: 0, fontSize: "24px", fontWeight: 800 };
const modalSubtitle = { margin: "8px 0 0", opacity: 0.85 };
const closeButton = { border: 0, background: "transparent", color: "#fff", fontSize: "38px", lineHeight: 1, cursor: "pointer" };
const modalBody = { padding: "28px", overflowY: "auto", WebkitOverflowScrolling: "touch" };
const fieldLabel = { display: "block", marginBottom: "8px", color: "#1f2937", fontSize: "14px", fontWeight: 700 };
const fieldInput = { width: "100%", height: "58px", padding: "0 16px", border: "1px solid #cbd5e1", borderRadius: "14px", fontSize: "16px", boxSizing: "border-box", marginBottom: "16px" };
const timeGrid = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" };
const timingHint = { margin: "-4px 0 16px", padding: "12px 14px", borderRadius: "14px", background: "#eff6ff", color: "#1d4ed8", fontSize: "13px", fontWeight: 700, border: "1px solid #bfdbfe" };
const scheduleHeader = { display: "grid", gap: "4px", margin: "8px 0 10px" };
const scheduleTitle = { fontSize: "14px", color: "#0f172a" };
const scheduleSubtitle = { fontSize: "12px", color: "#64748b" };
const scheduleActions = { display: "flex", justifyContent: "flex-start", marginBottom: "10px" };
const addExceptionButton = { height: "38px", padding: "0 14px", border: "1px solid #cbd5e1", borderRadius: "12px", background: "#fff", color: "#1d4ed8", fontWeight: 800, display: "inline-flex", alignItems: "center", gap: "8px", cursor: "pointer" };
const exceptionList = { display: "grid", gap: "10px", marginBottom: "16px" };
const exceptionRow = { display: "grid", gridTemplateColumns: "84px 1fr 1fr auto auto", gap: "8px", alignItems: "center" };
const exceptionDayInput = { height: "44px", border: "1px solid #cbd5e1", borderRadius: "12px", padding: "0 10px", fontSize: "14px" };
const exceptionTimeInput = { height: "44px", border: "1px solid #cbd5e1", borderRadius: "12px", padding: "0 10px", fontSize: "14px" };
const exceptionClosed = { height: "44px", display: "flex", alignItems: "center", gap: "8px", padding: "0 10px", border: "1px solid #cbd5e1", borderRadius: "12px", fontSize: "13px", color: "#334155", fontWeight: 700 };
const removeExceptionButton = { width: "42px", height: "42px", border: 0, borderRadius: "12px", background: "#fee2e2", color: "#dc2626", cursor: "pointer" };
const dayButton = { height: "48px", border: "1px solid #cbd5e1", background: "#fff", borderRadius: "12px", cursor: "pointer", fontWeight: 600 };
const dayActive = { ...dayButton, background: "#eff6ff", color: "#2563eb", borderColor: "#93c5fd" };
const closedDayRow = { display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "16px" };
const closedDaySelect = { height: "48px", minWidth: "130px", border: "1px solid #cbd5e1", background: "#fff", borderRadius: "12px", padding: "0 12px", cursor: "pointer", fontWeight: 700, color: "#334155" };
const checkboxRow = { display: "flex", alignItems: "center", gap: "10px", color: "#334155", fontWeight: 600, marginBottom: "20px" };
const footerRow = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" };
const cancelButton = { height: "56px", border: "1px solid #cbd5e1", background: "#fff", borderRadius: "14px", fontSize: "16px", fontWeight: 700, cursor: "pointer" };
const updateButton = { height: "56px", border: 0, background: "#316ce8", color: "#fff", borderRadius: "14px", fontSize: "16px", fontWeight: 700, cursor: "pointer" };

export default AdminMarkets;

const marketColors = ["#2563eb", "#9333ea", "#16a34a", "#ea580c", "#db2777", "#0891b2", "#dc2626", "#4f46e5"];

function getInitials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((item) => item[0])
    .join("")
    .toUpperCase() || "MK";
}

function formatTime(value) {
  if (!value) return "-";
  const [hour, minute] = String(value).split(":").map(Number);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return value;
  const date = new Date();
  date.setHours(hour, minute, 0, 0);
  return date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}

function sortMarketsByTime(left, right) {
  const leftOpen = timeStringToMinutes(left.open_time);
  const rightOpen = timeStringToMinutes(right.open_time);
  if (leftOpen !== rightOpen) return leftOpen - rightOpen;
  const leftClose = timeStringToMinutes(left.close_time);
  const rightClose = timeStringToMinutes(right.close_time);
  if (leftClose !== rightClose) return leftClose - rightClose;
  return String(left.name || "").localeCompare(String(right.name || ""));
}

function timeStringToMinutes(value) {
  const match = String(value || "").match(/^(\d{1,2}):(\d{2})/);
  if (!match) return Number.MAX_SAFE_INTEGER;
  return (Number(match[1]) * 60) + Number(match[2]);
}
